/** Form helpers for one onboarding-price row. Inputs are kept as strings while editing. */

export const FIELDS = ['stationPrice', 'pompiPrice', 'sizePerFillUp'];

export function toDraft(price) {
  return {
    stationPrice: String(price.stationPrice ?? ''),
    pompiPrice: String(price.pompiPrice ?? ''),
    sizePerFillUp: String(price.sizePerFillUp ?? ''),
  };
}

/** Accepts a Dutch decimal comma ("1,95") as well as a dot. Returns NaN when not a number. */
export function parseNumber(value) {
  const normalised = String(value).trim().replace(',', '.');
  if (!/^\d*\.?\d+$|^\d+\.$/.test(normalised)) return NaN;
  return Number(normalised);
}

export function toPayload(draft) {
  return {
    stationPrice: parseNumber(draft.stationPrice),
    pompiPrice: parseNumber(draft.pompiPrice),
    sizePerFillUp: parseNumber(draft.sizePerFillUp),
  };
}

/** True as soon as any of the three fields differs from the saved value as typed text. */
export function isDirty(draft, price) {
  const saved = toDraft(price);
  return FIELDS.some((f) => draft[f] !== saved[f]);
}

const MAX_PRICE = 99.99;
const MAX_SIZE = 1000;

function checkPrice(raw, label) {
  if (String(raw).trim() === '') return `Enter the ${label}.`;
  const n = parseNumber(raw);
  if (Number.isNaN(n)) return 'Enter a number, e.g. 1.95.';
  if (n <= 0) return 'Must be greater than 0.';
  if (n > MAX_PRICE) return `Must be €${MAX_PRICE} or less.`;
  if (!/^\d+(\.\d{1,3})?$/.test(String(n))) return 'Use at most 3 decimals.';
  return undefined;
}

/** Returns `{ field: message }` for every invalid field. */
export function validateDraft(draft, unit) {
  const errors = {};
  const station = checkPrice(draft.stationPrice, 'station price');
  const pompi = checkPrice(draft.pompiPrice, 'Pompi price');
  if (station) errors.stationPrice = station;
  if (pompi) errors.pompiPrice = pompi;
  if (!station && !pompi && parseNumber(draft.pompiPrice) >= parseNumber(draft.stationPrice)) {
    errors.pompiPrice = 'Pompi price must be lower than the station price.';
  }

  const rawSize = String(draft.sizePerFillUp).trim();
  const size = parseNumber(rawSize);
  if (rawSize === '') errors.sizePerFillUp = unit === 'kWh' ? 'Enter the charge size.' : 'Enter the tank size.';
  else if (Number.isNaN(size)) errors.sizePerFillUp = 'Enter a number, e.g. 50.';
  else if (size <= 0) errors.sizePerFillUp = 'Must be greater than 0.';
  else if (size > MAX_SIZE) errors.sizePerFillUp = `Must be ${MAX_SIZE} ${unit} or less.`;
  return errors;
}

/** Live diff/saving for a draft; null when the numbers aren't usable yet. */
export function computeSavings(draft) {
  const { stationPrice, pompiPrice, sizePerFillUp } = toPayload(draft);
  if ([stationPrice, pompiPrice, sizePerFillUp].some(Number.isNaN)) return null;
  const diff = stationPrice - pompiPrice;
  return { stationPrice, pompiPrice, sizePerFillUp, diff, perFillUp: diff * sizePerFillUp };
}
